import type {
  ConsultantRecommendationResponse,
  CosmosQueryResponse,
} from "@/lib/cosmos-api";
import type { ClientConversationExecutionSnapshot } from "@/lib/conversation-context";
import {
  resolveRowDescription,
  resolveRowTitle,
} from "@/lib/format";

export type CanvasTab = "summary" | "table" | "chart" | "sources";

export type WorkspaceCanvasMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  prompt?: string;
  status?: "thinking" | "complete" | "error";
  queryResult?: CosmosQueryResponse | null;
  queryError?: string | null;
  executionSnapshot?: ClientConversationExecutionSnapshot;
  consultantResult?: ConsultantRecommendationResponse | null;
  consultantError?: string | null;
};

export type WorkspaceCanvasView = {
  query: string;
  queryResult: CosmosQueryResponse | null;
  queryError: string | null;
  consultantResult: ConsultantRecommendationResponse | null;
  consultantError: string | null;
  executionSnapshot?: ClientConversationExecutionSnapshot;
  answerText: string;
  isHistoricalFallback: boolean;
};

export type SearchSourceGroup = {
  callId: string;
  query: string;
  index?: string;
  documents: CosmosQueryResponse["knowledgeContext"];
};

export const isCatalogSearchCanvasAnswer = (queryResult?: CosmosQueryResponse | null) =>
  queryResult?.queryExecution?.streamSource === "meilisearch" ||
  queryResult?.queryExecution?.engine === "meilisearch";

export const messageHasCanvasData = (message: WorkspaceCanvasMessage) =>
  message.role === "assistant" &&
  (Boolean(message.queryResult) ||
    Boolean(message.consultantResult) ||
    Boolean(message.queryError) ||
    Boolean(message.consultantError) ||
    (message.status === "complete" && message.content.trim().length > 0));

export const buildCanvasViewFromMessage = (
  message: WorkspaceCanvasMessage,
): WorkspaceCanvasView | null => {
  if (!messageHasCanvasData(message)) {
    return null;
  }

  const query = message.prompt ?? message.content;
  if (message.queryResult || message.consultantResult || message.queryError || message.consultantError) {
    return {
      query,
      queryResult: message.queryResult ?? null,
      queryError: message.queryError ?? null,
      consultantResult: message.consultantResult ?? null,
      consultantError: message.consultantError ?? null,
      executionSnapshot: message.executionSnapshot,
      answerText: message.content,
      isHistoricalFallback: false,
    };
  }

  return {
    query,
    queryResult: null,
    queryError: null,
    consultantResult: null,
    consultantError: null,
    executionSnapshot: message.executionSnapshot,
    answerText: message.content,
    isHistoricalFallback: true,
  };
};

export const canvasRowsForView = (view: WorkspaceCanvasView): CosmosQueryResponse["rows"] => {
  if (view.queryResult?.rows?.length) {
    return view.queryResult.rows;
  }
	if (isCatalogSearchCanvasAnswer(view.queryResult) && view.queryResult?.knowledgeContext?.length) {
    return knowledgeContextToRows(view.queryResult.knowledgeContext);
  }
  if (view.consultantResult?.sourceDocuments?.length) {
    return knowledgeContextToRows(view.consultantResult.sourceDocuments);
  }
  if (view.isHistoricalFallback && view.executionSnapshot?.entities.length) {
    return view.executionSnapshot.entities.map((entity) => ({
      id: entity.id,
      title: entity.title,
      entityType: entity.entityType,
      description: entity.description,
      durationMinutes: entity.durationMinutes,
      format: entity.format,
      status: entity.status,
      ...(entity.metadata ?? {}),
    }));
  }
  return [];
};

export const knowledgeContextToRows = (
  knowledgeContext: CosmosQueryResponse["knowledgeContext"],
): CosmosQueryResponse["rows"] =>
  knowledgeContext.map((source) => {
    const metadata = source.metadata ?? {};
    const searchCallId =
      typeof metadata.meilisearchCallId === "string" ? metadata.meilisearchCallId : undefined;
    const searchQuery =
      typeof metadata.meilisearchQuery === "string" ? metadata.meilisearchQuery : undefined;
    const searchIndex =
      typeof metadata.meilisearchIndex === "string" ? metadata.meilisearchIndex : undefined;

    return {
      sourceId: source.sourceId,
      sourceType: source.sourceType,
      title: source.title,
      confidence: source.confidence,
      snippet: source.snippet,
      uri: source.uri,
      url: source.uri,
      ...metadata,
      ...(searchCallId ? { searchCallId } : {}),
      ...(searchQuery ? { searchQuery } : {}),
      ...(searchIndex ? { searchIndex } : {}),
    };
  });

export const groupSearchSources = (
  knowledgeContext: CosmosQueryResponse["knowledgeContext"] = [],
): SearchSourceGroup[] => {
	const groups = new Map<string, SearchSourceGroup>();

  for (const source of knowledgeContext) {
    const metadata = source.metadata ?? {};
    const callId =
      typeof metadata.meilisearchCallId === "string"
        ? metadata.meilisearchCallId
        : "ungrouped";
    const query =
      typeof metadata.meilisearchQuery === "string"
        ? metadata.meilisearchQuery
        : "Retrieved documents";
    const index =
      typeof metadata.meilisearchIndex === "string"
        ? metadata.meilisearchIndex
        : undefined;
    const key = `${callId}::${query}::${index ?? ""}`;
    const existing = groups.get(key);
    if (existing) {
      existing.documents.push(source);
      continue;
    }
    groups.set(key, {
      callId,
      query,
      index,
      documents: [source],
    });
  }

  return [...groups.values()];
};

export type CanvasDataTypeCount = {
  label: string;
  count: number;
};

export const resolveRowDataType = (row: CosmosQueryResponse["rows"][number]) => {
  if (typeof row.type === "string" && row.type.trim().length > 0) {
    return row.type;
  }
  if (typeof row.entityType === "string" && row.entityType.trim().length > 0) {
    return row.entityType;
  }
  if (typeof row.sourceType === "string" && row.sourceType.trim().length > 0) {
    return sourceSystemLabel(row.sourceType);
  }
  if (typeof (row as { Type?: unknown }).Type === "string") {
    return (row as { Type: string }).Type;
  }
  return "Other";
};

export const resolveSourceDataType = (
  source: CosmosQueryResponse["knowledgeContext"][number],
) => {
  const metadataType = source.metadata?.type;
  if (typeof metadataType === "string" && metadataType.trim().length > 0) {
    return metadataType;
  }
  return sourceSystemLabel(source.sourceType);
};

export const buildDataTypeCounts = (
  rows: CosmosQueryResponse["rows"],
): CanvasDataTypeCount[] => {
  const counts = rows.reduce<Record<string, number>>((acc, row) => {
    const label = resolveRowDataType(row);
    acc[label] = (acc[label] ?? 0) + 1;
    return acc;
  }, {});

  return Object.entries(counts)
    .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
    .map(([label, count]) => ({ label, count }));
};

export const buildSourceTypeChartData = (rows: CosmosQueryResponse["rows"]) => {
  const counts = buildDataTypeCounts(rows);
  if (counts.length === 0) {
    return [{ label: "No documents", value: 0 }];
  }
  return counts.map(({ label, count }) => ({ label, value: count }));
};

export const sourceDocumentToJson = (
  source: CosmosQueryResponse["knowledgeContext"][number],
): Record<string, unknown> => {
  const metadata =
    source.metadata && typeof source.metadata === "object"
      ? { ...(source.metadata as Record<string, unknown>) }
      : {};
  const {
    meilisearchCallId: _callId,
    meilisearchQuery: _query,
    meilisearchIndex: _index,
    ...documentFields
  } = metadata;

  return {
    ...documentFields,
    sourceId: source.sourceId,
    sourceType: sourceSystemLabel(source.sourceType),
    title: source.title,
    confidence: source.confidence,
    ...(source.snippet ? { snippet: source.snippet } : {}),
    ...(source.uri ? { uri: source.uri, url: source.uri } : {}),
  };
};

export const rowDocumentToJson = (
  row: CosmosQueryResponse["rows"][number],
): Record<string, unknown> => {
  const {
    meilisearchCallId: _callId,
    meilisearchQuery: _query,
    meilisearchIndex: _index,
    sourceTable: _sourceTable,
    ...document
  } = row;
  return {
    ...document,
    ...(typeof row.sourceType === "string"
      ? { sourceType: sourceSystemLabel(row.sourceType) }
      : {}),
  };
};

export const buildNumericChartData = (rows: CosmosQueryResponse["rows"]) => {
  if (rows.length === 0) {
    return [{ label: "No rows", value: 0 }];
  }

  const entityCounts = rows.reduce<Record<string, number>>((counts, row) => {
    if (typeof row.entityType === "string") {
      counts[row.entityType] = (counts[row.entityType] ?? 0) + 1;
    }
    return counts;
  }, {});

  if (Object.keys(entityCounts).length > 0) {
    return Object.entries(entityCounts).map(([label, value]) => ({ label, value }));
  }

  const firstNumericKey = Object.keys(rows[0] ?? {}).find(
    (key) => typeof rows[0]?.[key] === "number",
  );
  if (!firstNumericKey) {
    return rows.map((row, index) => ({
      label: resolveRowTitle(row) === "Result" ? `Row ${index + 1}` : resolveRowTitle(row),
      value: 1,
    }));
  }

  return rows.map((row, index) => ({
    label: resolveRowTitle(row) === "Result" ? `Row ${index + 1}` : resolveRowTitle(row),
    value: Number(row[firstNumericKey]),
  }));
};

export const defaultCanvasTabForView = (view: WorkspaceCanvasView): CanvasTab => {
  if (view.consultantResult) {
    return "summary";
  }
  const planned = view.queryResult?.presentationPlan?.defaultTab;
  if (planned) {
    return planned;
  }
	if (isCatalogSearchCanvasAnswer(view.queryResult)) {
    return (view.queryResult?.knowledgeContext?.length ?? 0) > 0 ? "sources" : "summary";
  }
  if ((view.queryResult?.rows?.length ?? 0) > 0) {
    return "table";
  }
  return "summary";
};

export const sourceSystemLabel = (sourceType: string) => {
  switch (sourceType) {
    case "course-content":
      return "Course content";
    case "blog-post":
      return "Blog";
    case "meilisearch":
      return "Indexed content";
    default:
      return "Internal docs";
  }
};

export const formatModelRoutingLabel = (provider?: string, model?: string) => {
  if (provider === "meilisearch" || model?.toLowerCase().includes("meilisearch")) {
    return "Cosmos · knowledge search";
  }
  if (!provider || !model) {
    return provider ?? model ?? "";
  }
  return `${provider} · ${model}`;
};

export const formatStreamSourceLabel = (streamSource?: string) => {
  switch (streamSource) {
    case "meilisearch":
      return "Knowledge search";
    case "bedrock":
      return "AI pipeline";
    default:
      return streamSource ?? "";
  }
};

export const formatQueryEngineLabel = (engine?: string) => {
  switch (engine) {
    case "meilisearch":
      return "Knowledge search";
    default:
      return engine ?? "";
  }
};

export const summarizeCanvasRows = (rows: CosmosQueryResponse["rows"]) => {
  const entityCounts = rows.reduce<Record<string, number>>((counts, row) => {
    const entityType =
      typeof row.entityType === "string"
        ? row.entityType
        : typeof row.type === "string"
          ? row.type
          : "result";
    counts[entityType] = (counts[entityType] ?? 0) + 1;
    return counts;
  }, {});

  const ratings = rows
    .map((row) => row.ratingAverage)
    .filter((rating): rating is number => typeof rating === "number");
  const averageRating =
    ratings.length > 0
      ? ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length
      : undefined;

  return {
    entityCounts,
    averageRating,
    linkedSources: rows.some((row) => typeof row.url === "string" || typeof row.uri === "string"),
  };
};

export const topCanvasCards = (
  view: WorkspaceCanvasView,
  limit = 8,
): CosmosQueryResponse["rows"] => canvasRowsForView(view).slice(0, limit);

export const metadataBadges = (metadata?: Record<string, unknown>) => {
  const badges: Array<{ tone: "info" | "ai" | "success"; label: string }> = [];
  if (typeof metadata?.type === "string") {
    badges.push({ tone: "info", label: metadata.type });
  }
  if (
    metadata?.parent &&
    typeof metadata.parent === "object" &&
    "title" in metadata.parent
  ) {
    badges.push({
      tone: "ai",
      label: `Parent · ${String((metadata.parent as { title?: unknown }).title ?? "Unknown")}`,
    });
  }
  if (typeof metadata?.sku === "string") {
    badges.push({ tone: "success", label: metadata.sku });
  }
  if (typeof metadata?.LicenseId === "string") {
    badges.push({ tone: "success", label: metadata.LicenseId });
  }
  return badges;
};

export const cardDescription = (row: CosmosQueryResponse["rows"][number]) => {
  const raw = resolveRowDescription(row);
  return raw ? raw.slice(0, 220) : undefined;
};

export const cardTitle = (row: CosmosQueryResponse["rows"][number], index: number) => {
  const title = resolveRowTitle(row);
  return title === "Result" ? `Result ${index + 1}` : title;
};

export const cardUrl = (row: CosmosQueryResponse["rows"][number]) => {
  const url = row.url ?? row.uri;
  return typeof url === "string" ? url : undefined;
};
